import React from 'react';
import { motion, type Variants } from 'framer-motion';

/** Framer-style ease: fast out of the gate, long soft landing. */
export const EASE = [0.22, 1, 0.36, 1] as const;

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

export const stagger = (gap = 0.08, delay = 0): Variants => ({
  hidden: {},
  show: { transition: { staggerChildren: gap, delayChildren: delay } },
});

type RevealProps = React.ComponentProps<typeof motion.div> & { delay?: number };

/** Fades and lifts its children into place the first time they scroll into view. */
export const Reveal = ({ delay = 0, children, ...rest }: RevealProps) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: '0px 0px -12% 0px' }}
    variants={{
      hidden: { opacity: 0, y: 28 },
      show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE, delay } },
    }}
    {...rest}
  >
    {children}
  </motion.div>
);

/** A container whose direct `RevealItem` children animate in one after another. */
export const RevealGroup = ({ gap = 0.08, delay = 0, children, ...rest }: React.ComponentProps<typeof motion.div> & { gap?: number; delay?: number }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: '0px 0px -10% 0px' }}
    variants={stagger(gap, delay)}
    {...rest}
  >
    {children}
  </motion.div>
);

export const RevealItem = ({ children, ...rest }: React.ComponentProps<typeof motion.div>) => (
  <motion.div variants={fadeUp} {...rest}>
    {children}
  </motion.div>
);
